import {
  closureHit,
  dependencyApplies,
  gapMin,
  overlaps,
  staticBlock,
  staticCostParts,
  type AirportConfig,
  type StandAssignment,
  type StandClosure,
  type Turn,
} from "@yb/core";
import { label } from "./format";

export type DropLevel = "ok" | "soft" | "bad";
export interface DropCheck {
  level: DropLevel;
  reason: string;
}

/** Same rules as the server's optimizer, evaluated in the browser while dragging. */
export function checkDrop(
  turn: Turn,
  standId: string,
  turns: Turn[],
  stands: Record<string, StandAssignment>,
  config: AirportConfig,
  closures: StandClosure[],
): DropCheck {
  const stand = config.stands.find((s) => s.id === standId);
  if (!stand) return { level: "bad", reason: "Unknown stand" };
  const block = staticBlock(turn, stand, config);
  if (block) return { level: "bad", reason: block.text };
  const closure = closureHit(turn, standId, closures);
  if (closure) return { level: "bad", reason: `Closed: ${closure.reason}` };
  for (const other of turns) {
    if (other.id === turn.id) continue;
    const sid = stands[other.id]?.standId;
    if (sid === standId) {
      const gap = gapMin(turn, other);
      if (gap < config.settings.minBufferMin) {
        return {
          level: "bad",
          reason: gap < 0 ? `Clashes with ${label(other)}` : `Only ${Math.round(gap)} min after/before ${label(other)}`,
        };
      }
    } else if (sid && overlaps(turn.start, turn.end, other.start, other.end)) {
      if (dependencyApplies(config, standId, turn, sid) || dependencyApplies(config, sid, other, standId)) {
        return { level: "bad", reason: `Blocked by ${label(other)} on ${sid}` };
      }
    }
  }
  const soft = staticCostParts(turn, stand, config);
  if (soft.length) return { level: "soft", reason: soft.map((s) => s.text).join(" · ") };
  return { level: "ok", reason: stand.jetbridge ? "Fits · jet bridge" : "Fits" };
}
